import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { orderNumber } from '@utils';

import OrderDisplayMappingForm from './OrderDisplayMappingForm';

const message = defineMessages({
    objectName: 'ReadyBoard Mapping',
});

const OrderDisplayMappingListPage = () => {
    const translate = useTranslate();
    const location = useLocation();
    const params = useParams();
    const searchParams = new URLSearchParams(location.search);
    const orderDisplayId = searchParams.get('orderDisplayId');
    const orderDisplayName = searchParams.get('orderDisplayName');
    const deviceId = searchParams.get('deviceId');
    const deviceName = searchParams.get('deviceName');
    const deviceTabPath =
        generatePath(routes.listOrderDeviceTabPage.path, { customerId: params.customerId }) +
        `?deviceId=${deviceId}&deviceName=${encodeURIComponent(deviceName || '')}`;

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        detail,
        loadingSave,
        form,
        openModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
    } = useListModal({
        apiConfig: {
            getList: apiConfig.orderDisplayMapping.getList,
            getById: apiConfig.orderDisplayMapping.getById,
            create: apiConfig.orderDisplayMapping.create,
            update: apiConfig.orderDisplayMapping.update,
            delete: apiConfig.orderDisplayMapping.delete,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.prepareGetListParams = (filter) => {
                return {
                    ...filter,
                    page: filter?.page > 0 ? filter.page - 1 : 0,
                    size: DEFAULT_TABLE_ITEM_SIZE,
                    orderDisplayId,
                };
            };
        },
    });

    const handleSubmitForm = (values) => {
        const payload = { ...values };
        if (!detail?.id) {
            payload.orderDisplayId = orderDisplayId;
        }
        onSave(payload);
    };

    const columns = [
        {
            title: '#',
            dataIndex: 'index',
            key: 'id',
            width: 50,
            render: (text, record, index) => orderNumber(pagination, index),
        },
        {
            title: translate.formatMessage(commonMessage.readyBoardMappingNumber),
            dataIndex: 'orderDisplayNumber',
            render: (data) => {
                return (
                    <>
                        {translate.formatMessage(commonMessage.readyBoardMappingNumber)} {data}
                    </>
                );
            },
        },

        mixinFuncs.renderActionColumn(
            {
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ].filter(Boolean);

    return (
        <PageWrapper
            routes={[
                {
                    breadcrumbName: translate.formatMessage(commonMessage.managerOrderDisplay),
                    path: routes.listOrderDevicePage.path,
                },
                {
                    breadcrumbName: deviceName,
                    path: deviceTabPath,
                },
                {
                    breadcrumbName: orderDisplayName || translate.formatMessage(message.objectName),
                },
            ]}
        >
            <ListPage
                actionBar={mixinFuncs.renderActionBar()}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        loading={loading}
                        rowKey="id"
                    />
                }
            />
            {openModal && (
                <BaseModal
                    title={
                        detail
                            ? translate.formatMessage(commonMessage.update) +
                              ' ' +
                              translate.formatMessage(message.objectName)
                            : translate.formatMessage(commonMessage.create) +
                              ' ' +
                              translate.formatMessage(message.objectName)
                    }
                    width={400}
                    isModalVisible={openModal}
                    handleCancel={onCancelModal}
                    isEditing={!!detail}
                    form={form}
                    loading={loadingSave}
                >
                    <OrderDisplayMappingForm
                        detail={detail}
                        form={form}
                        isEditing={isEditing}
                        onSubmit={handleSubmitForm}
                        actions={mixinFuncs.renderActions()}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default OrderDisplayMappingListPage;
